import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import type { Sale, SaleInsert, SaleUpdate, SaleLineItemInsert } from '../lib/database.types'
import { SALE_LINE_ITEMS_KEY } from './useSaleLineItems'
import { ACTIVITY_LOG_KEY, logActivity } from './useActivityLog'
import { CONSIGNEE_INVENTORY_KEY } from './useConsigneeInventory'

export const SALES_KEY = ['sales']

export function useSales() {
  return useQuery({
    queryKey: SALES_KEY,
    queryFn: async (): Promise<Sale[]> => {
      const { data, error } = await supabase
        .from('sales')
        .select('*')
        .order('sale_date', { ascending: false })
      if (error) throw error
      return data ?? []
    },
  })
}

export interface CreateSaleInput {
  sale: Omit<SaleInsert, 'user_id'>
  lineItems?: Array<Omit<SaleLineItemInsert, 'user_id' | 'sale_id'>>
}

export function useCreateSale() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ sale, lineItems }: CreateSaleInput): Promise<Sale> => {
      const {
        data: { user },
      } = await supabase.auth.getUser()
      if (!user) throw new Error('not authenticated')

      const { data: createdSale, error } = await supabase
        .from('sales')
        .insert({ ...sale, user_id: user.id })
        .select('*')
        .single()
      if (error) throw error

      if (lineItems && lineItems.length > 0) {
        const rows = lineItems.map((li) => ({
          ...li,
          user_id: user.id,
          sale_id: createdSale.id,
        }))
        const { error: liErr } = await supabase.from('sale_line_items').insert(rows)
        if (liErr) {
          // Rollback the sale to avoid an orphan row. If cleanup itself
          // fails, surface both errors so the user knows manual cleanup
          // is needed instead of just seeing the line-item failure.
          const { error: rollbackErr } = await supabase.from('sales').delete().eq('id', createdSale.id)
          if (rollbackErr) {
            throw new Error(
              `Sale line-items failed (${liErr.message}); rollback also failed (${rollbackErr.message}). Orphan sale id=${createdSale.id} may need manual cleanup.`,
            )
          }
          throw liErr
        }
      }

      const totalQty = (lineItems ?? []).reduce((s, li) => s + Number(li.quantity), 0)
      void logActivity({
        user_id: user.id,
        actor_user_id: user.id,
        entity_type: 'sale',
        entity_id: createdSale.id,
        action: 'created',
        metadata: {
          gross_amount: createdSale.gross_amount,
          platform: createdSale.platform,
          customer_name: createdSale.customer_name,
          sale_date: createdSale.sale_date,
          units: totalQty,
        },
      })

      return createdSale
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SALES_KEY })
      queryClient.invalidateQueries({ queryKey: SALE_LINE_ITEMS_KEY })
      queryClient.invalidateQueries({ queryKey: ACTIVITY_LOG_KEY })
    },
  })
}

export function useUpdateSale() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string } & SaleUpdate): Promise<Sale> => {
      const { data, error } = await supabase
        .from('sales')
        .update(updates)
        .eq('id', id)
        .select('*')
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SALES_KEY })
      // A sale's totals feed into line-item revenue + (for consignee sales)
      // the consignee dashboard. Refresh both so derived views don't drift.
      queryClient.invalidateQueries({ queryKey: SALE_LINE_ITEMS_KEY })
      queryClient.invalidateQueries({ queryKey: CONSIGNEE_INVENTORY_KEY })
    },
  })
}

export function useDeleteSale() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      // Snapshot the sale so we can describe it in the activity log
      // after the row itself is gone. Throwing on the read error means a
      // missing/concurrent-deleted sale fails fast instead of proceeding
      // with a destructive delete + a content-less activity entry.
      const { data: sale, error: readErr } = await supabase
        .from('sales')
        .select('id, gross_amount, platform, customer_name, sale_date, user_id')
        .eq('id', id)
        .single()
      if (readErr) throw readErr

      const { error } = await supabase.from('sales').delete().eq('id', id)
      if (error) throw error

      if (sale) {
        void logActivity({
          user_id: sale.user_id,
          actor_user_id: sale.user_id,
          entity_type: 'sale',
          entity_id: id,
          action: 'deleted',
          metadata: {
            gross_amount: sale.gross_amount,
            platform: sale.platform,
            customer_name: sale.customer_name,
            sale_date: sale.sale_date,
          },
        })
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SALES_KEY })
      queryClient.invalidateQueries({ queryKey: SALE_LINE_ITEMS_KEY })
      queryClient.invalidateQueries({ queryKey: ACTIVITY_LOG_KEY })
      // Inventory recovers (line items cascade-deleted with the sale).
      queryClient.invalidateQueries({ queryKey: ['products'] })
      // Consignee inventory may have been impacted if the deleted sale
      // was a consignee one — refresh to keep mom's tile counts honest.
      queryClient.invalidateQueries({ queryKey: CONSIGNEE_INVENTORY_KEY })
    },
  })
}

/**
 * Toggle a sale's is_refunded flag. When set, the sale:
 *  - contributes \$0 to income (handled by computeProfitForPeriod)
 *  - line items are excluded from inventory math (units return to stock)
 *
 * Pass `refunded: true` to mark refunded, `false` to undo a previous refund.
 */
export function useSetSaleRefunded() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, refunded }: { id: string; refunded: boolean }): Promise<Sale> => {
      // Consignee sales need an inventory adjustment on the same transaction
      // as the is_refunded flip, so route through the SECURITY DEFINER RPC.
      // Plain owner-created sales still use the direct update.
      const { data: existing, error: readErr } = await supabase
        .from('sales')
        .select('consignee_id')
        .eq('id', id)
        .single()
      if (readErr) throw readErr

      let data: Sale
      if (existing.consignee_id) {
        const { data: rpcData, error: rpcErr } = await supabase.rpc('set_consignee_sale_refunded', {
          p_sale_id: id,
          p_refunded: refunded,
        })
        if (rpcErr) throw rpcErr
        data = rpcData as unknown as Sale
      } else {
        const { data: updData, error } = await supabase
          .from('sales')
          .update({ is_refunded: refunded })
          .eq('id', id)
          .select('*')
          .single()
        if (error) throw error
        data = updData
      }

      void logActivity({
        user_id: data.user_id,
        actor_user_id: data.user_id,
        entity_type: 'sale',
        entity_id: data.id,
        action: refunded ? 'refunded' : 'unrefunded',
        metadata: {
          gross_amount: data.gross_amount,
          platform: data.platform,
          customer_name: data.customer_name,
          sale_date: data.sale_date,
        },
      })

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SALES_KEY })
      queryClient.invalidateQueries({ queryKey: ACTIVITY_LOG_KEY })
      // Inventory depends on which sales count as sold.
      queryClient.invalidateQueries({ queryKey: ['products'] })
      // Consignee tile counts ride on consignee_inventory.
      queryClient.invalidateQueries({ queryKey: CONSIGNEE_INVENTORY_KEY })
    },
  })
}

export function saleNetRevenue(s: Sale): number {
  return Number(s.gross_amount) + Number(s.shipping_charged) - Number(s.platform_fees) - Number(s.shipping_cost)
}
